import React, { useState } from 'react';
import { MessageCircle, X, ShieldCheck, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import contentData from '../data/website_content.json';

export const FloatingWhatsAppCTA: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { brand } = contentData;

  const abdul = brand.developers[0];
  const aswad = brand.developers[1];

  return (
    <div className="fixed bottom-6 left-6 z-40 hidden sm:flex flex-col items-start">
      {/* Popover Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 15 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="mb-3 w-80 sm:w-96 rounded-3xl bg-charcoal/95 backdrop-blur-2xl border border-white/10 shadow-2xl p-5 text-soft-white shadow-black/80 relative"
          >
            {/* Header */}
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-jade/15 border border-jade/30 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-jade" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-ivory flex items-center gap-1.5">
                    Direct Founder Chat
                    <span className="w-2 h-2 rounded-full bg-jade animate-pulse" />
                  </h4>
                  <div className="text-[10px] font-mono text-muted">Response within ~5 minutes</div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-lg text-muted hover:text-ivory hover:bg-white/5 transition-colors"
                aria-label="Close WhatsApp Drawer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Introduction Prompt */}
            <p className="text-xs text-muted leading-relaxed mb-4">
              Have questions about batch Facebook Marketplace posting, account safety, or workstation activation? Chat directly with the developers:
            </p>

            {/* Founder 1: Abdul Mueed Lashary */}
            <div className="space-y-2">
              <a
                href={abdul.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-2xl bg-plum-black/80 border border-white/10 hover:border-rose/30 transition-all flex items-center justify-between group"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose to-vermilion flex items-center justify-center text-white font-bold text-xs">
                    AML
                  </div>
                  <div>
                    <div className="text-xs font-bold text-ivory group-hover:text-rose transition-colors flex items-center gap-1.5">
                      {abdul.name}
                      <span className="text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded bg-rose/15 text-rose">
                        Tech Lead
                      </span>
                    </div>
                    <div className="text-[10px] text-muted font-mono">{abdul.whatsappDisplay}</div>
                  </div>
                </div>
                <div className="w-7 h-7 rounded-xl bg-jade/15 border border-jade/30 flex items-center justify-center text-jade group-hover:scale-110 transition-transform">
                  <ExternalLink className="w-3.5 h-3.5" />
                </div>
              </a>

              {/* Founder 2: Aswad Ammar Lashary */}
              <a
                href={aswad.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-2xl bg-plum-black/80 border border-white/10 hover:border-orchid/30 transition-all flex items-center justify-between group"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orchid to-wine flex items-center justify-center text-white font-bold text-xs">
                    AAL
                  </div>
                  <div>
                    <div className="text-xs font-bold text-ivory group-hover:text-orchid transition-colors flex items-center gap-1.5">
                      {aswad.name}
                      <span className="text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded bg-orchid/15 text-orchid">
                        Setup Lead
                      </span>
                    </div>
                    <div className="text-[10px] text-muted font-mono">{aswad.whatsappDisplay}</div>
                  </div>
                </div>
                <div className="w-7 h-7 rounded-xl bg-jade/15 border border-jade/30 flex items-center justify-center text-jade group-hover:scale-110 transition-transform">
                  <ExternalLink className="w-3.5 h-3.5" />
                </div>
              </a>
            </div>

            {/* Trust Footer */}
            <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[10px] font-mono text-muted">
              <span className="flex items-center gap-1 text-jade">
                <ShieldCheck className="w-3 h-3" />
                Verified Developers
              </span>
              <span>Karachi, Pakistan (PKT)</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Launcher Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-jade to-teal text-obsidian font-bold text-xs shadow-xl shadow-black/50 border border-jade/30 relative group cursor-pointer"
        aria-label="Direct WhatsApp Contact"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-obsidian opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-obsidian" />
        </span>
        <MessageCircle className="w-4 h-4 fill-obsidian text-jade" />
        <span className="tracking-tight uppercase font-mono text-[11px]">Chat on WhatsApp</span>
      </motion.button>
    </div>
  );
};
